import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import { RUN_DINAR_URL, RUN_B2B_URL, getHeaders } from "../../Config";

const Sincronizacion = () => {
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [logs, setLogs] = useState([]);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [processedItems, setProcessedItems] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const toast = useRef(null);
    const eventSourceRef = useRef(null); 
    const timerRef = useRef(null);
    const startTimeRef = useRef(null);
    const isCompletedRef = useRef(false);
    const logsEndRef = useRef(null);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopSync();
        };
    }, []);

    // Auto-scroll console
    useEffect(() => {
        if (logsEndRef.current) {
            logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [logs]);

    const formatTime = (milliseconds) => {
        const mins = Math.floor(milliseconds / 60000);
        const secs = Math.floor((milliseconds % 60000) / 1000);
        const ms = Math.floor((milliseconds % 1000) / 10);
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
    };

    const addLog = (message, type = 'info') => {
        const timestamp = new Date().toLocaleTimeString();
        setLogs(prev => [...prev, { timestamp, message, type }]);
    };

    const stopSync = () => {
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        setLoading(false);
    };

    const handleSync = async (targetUrl) => {
        if (loading) return;

        // Reset state
        setLoading(true);
        setProgress(0);
        setLogs([]);
        setElapsedTime(0);
        setProcessedItems(0);
        setTotalItems(0);
        isCompletedRef.current = false;
        
        // Start timer
        startTimeRef.current = Date.now();
        timerRef.current = setInterval(() => {
            setElapsedTime(Date.now() - startTimeRef.current);
        }, 10);

        toast.current.show({ severity: 'info', summary: 'Iniciando', detail: 'Sincronización iniciada', life: 3000 });
        addLog('Iniciando proceso de sincronización...', 'info');

        let url = targetUrl;
        if (url) url = url.trim();
        
        // CORS WORKAROUND:
        // En desarrollo (localhost), convierte cualquier URL absoluta a ruta relativa
        // para que el proxy de CRA (package.json) la redirija al backend y evitar CORS.
        if (window.location.hostname === 'localhost' && /^https?:\/\//.test(url)) {
            try {
                const u = new URL(url);
                //const originalUrl = url;
                url = u.pathname + u.search;
                //addLog(`Dev Mode: Usando Proxy (URL relativa) para evitar CORS. ${originalUrl} -> ${url}`, 'info');
            } catch (e) {
                console.warn("No se pudo parsear la URL para proxy:", e);
            }
        }

        // PROD WORKAROUND: si la app corre en un dominio distinto al del backend,
        // intenta usar ruta relativa para aprovechar un reverse proxy del servidor.
        // Requiere que el servidor sirva /api/* hacia el backend.
        if (/^https?:\/\//.test(url)) {
            try {
                const u = new URL(url);
                if (window.location.hostname !== u.hostname) {
                    const originalUrl = url;
                    url = u.pathname + u.search;
                    addLog(`Prod Mode: Usando ruta relativa para evitar CORS. ${originalUrl} -> ${url}`, 'info');
                }
            } catch (e) {}
        }


        if (window.location.protocol === 'https:' && url.startsWith('http:')) {
            addLog('Advertencia: Intentando conectar a servicio HTTP desde HTTPS. Esto puede ser bloqueado por el navegador (Mixed Content).', 'error');
        }

        if (!url) {
            addLog('Error: URL de servicio no configurada (REACT_APP_RUN_DINAR_URL)', 'error');
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Configuración faltante' });
            stopSync();
            return;
        }

        try {
            
            // Usamos fetch en lugar de EventSource para poder enviar headers (Auth)
            const headers = getHeaders();
            headers.delete('Content-Type');
            headers.append('Accept', 'text/event-stream');
            const response = await fetch(url, { method: 'GET', headers, cache: 'no-store' });

            if (!response.ok) {
                throw new Error(`Error HTTP: ${response.status} ${response.statusText}`);
            }

            if (!response.body) {
                throw new Error('ReadableStream no soportado en este navegador.');
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');
            let buffer = '';

            addLog('Conexión establecida. Recibiendo datos...', 'success');

            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    break;
                }

                const chunk = decoder.decode(value, { stream: true });

                // Check for HTML response (proxy error or 404)
                if (buffer.length < 500 && (chunk.trim().startsWith('<!DOCTYPE') || chunk.trim().startsWith('<html'))) {
                    throw new Error('Respuesta inválida (HTML recibido). Verifica la configuración del proxy o la URL del backend.');
                }
                
                buffer += chunk;
                
                // Procesar líneas completas
                const lines = buffer.split('\n');
                // Guardar el último fragmento (posiblemente incompleto) en el buffer
                buffer = lines.pop();

                for (const line of lines) {
                    const trimmedLine = line.trim();
                    if (!trimmedLine) continue;

                    // Ignorar comentarios SSE y HTML (respuesta errónea del proxy/servidor)
                    if (trimmedLine.startsWith(':') || trimmedLine.startsWith('<') || trimmedLine.includes('doctype html') || trimmedLine.includes('xml version')) continue;

                    // Limpiar prefijos SSE estándar si existen
                    let cleanLine = trimmedLine;
                    if (cleanLine.startsWith('data:')) cleanLine = cleanLine.substring(5).trim();
                    if (cleanLine.startsWith('event:')) cleanLine = cleanLine.substring(6).trim();

                    // Loguear solo si hay contenido relevante
                    if (cleanLine) {
                        // Parsear progreso: busca patrón "número/número" (ej: "1/100", " 1 / 100 ")
                    const match = cleanLine.match(/(\d+)\s*\/\s*(\d+)/);
                    if (match) {
                        const current = parseInt(match[1], 10);
                        const total = parseInt(match[2], 10);
                        
                        setProcessedItems(current);
                        setTotalItems(total);
                        
                        if (total > 0) {
                            const percent = Math.round((current / total) * 100);
                            setProgress(percent);
                        }
                    }

                    // Loguear mensaje
                    addLog(cleanLine, 'info');

                    // Detectar finalización
                        if (cleanLine.includes('PROCESO TERMINADO') || cleanLine === 'terminado' || cleanLine === 'done') {
                            handleCompletion();
                        }
                    }
                }
            }
            
            // Al finalizar el stream, si quedó algo en el buffer
            if (buffer.trim()) {
                const cleanLine = buffer.replace(/^data:\s*/, '').trim();
                addLog(cleanLine, 'info');
                if (cleanLine.includes('PROCESO TERMINADO') || cleanLine === 'terminado') {
                    handleCompletion();
                }
            }

            // Si el stream termina naturalmente
            if (!isCompletedRef.current) {
                addLog('Stream finalizado por el servidor.', 'info');
                handleCompletion();
            }

        } catch (error) {
            console.error('Error durante la sincronización:', error);
            if (!isCompletedRef.current) {
                addLog(`Error: ${error.message}`, 'error');
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'Falló la sincronización' });
                stopSync();
            }
        }
    };

    const handleCompletion = () => {
        if (isCompletedRef.current) return;
        isCompletedRef.current = true;
        stopSync();
        setProgress(100);
        addLog('Proceso finalizado correctamente.', 'success');
        toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Sincronización completada' });
    };

    const itemsPerSecond = elapsedTime > 0 ? (processedItems / (elapsedTime / 1000)) : 0;

    return (
        <div className="surface-ground px-4 py-5 md:px-6 lg:px-8">
            <Toast ref={toast} />
            
            <div className="surface-card p-4 shadow-2 border-round-xl max-w-6 mx-auto">
                <div className="text-center mb-5">
                    <div className="text-900 text-3xl font-medium mb-3">Sincronización de Productos</div>
                    <span className="text-600 font-medium line-height-3">Ejecuta y monitorea el proceso de actualización en tiempo real</span>
                </div>

                <div className="flex flex-column gap-5">
                    {/* Control Section */}
                    <div className="flex flex-column align-items-center gap-3">
                        <div className="flex gap-2 w-full md:w-auto flex-column md:flex-row">
                            <Button 
                                label={loading ? "Sincronizando..." : "Sincronizar Planificador"} 
                                icon={loading ? "pi pi-spin pi-spinner" : "pi pi-play"} 
                                onClick={() => handleSync(RUN_DINAR_URL)} 
                                disabled={loading} 
                                className={`p-button-rounded p-button-lg ${loading ? 'p-button-secondary' : 'p-button-primary'} flex-1 md:flex-none px-4`}
                            />
                            <Button 
                                label={loading ? "Sincronizando..." : "Sincronizar B2B"} 
                                icon={loading ? "pi pi-spin pi-spinner" : "pi pi-globe"} 
                                onClick={() => handleSync(RUN_B2B_URL)} 
                                disabled={loading} 
                                className={`p-button-rounded p-button-lg ${loading ? 'p-button-secondary' : 'p-button-help'} flex-1 md:flex-none px-4`}
                            />
                        </div>
                        
                        <div className="flex align-items-center gap-2 text-700">
                            <i className="pi pi-clock text-xl"></i>
                            <span className="text-xl font-semibold font-monospace">{formatTime(elapsedTime)}</span>
                        </div>
                    </div>

                    {/* Progress Section */}
                    <div className="w-full">
                        <div className="flex justify-content-between mb-2">
                            <span className="text-600 font-medium text-sm">Progreso General</span>
                            <span className="text-900 font-bold text-sm">{progress}%</span>
                        </div>
                        <ProgressBar 
                            mode={loading ? 'indeterminate' : 'determinate'} 
                            value={progress} 
                            showValue={false} 
                            className="border-round-sm progress-bar-custom"
                            color="#6366f1"
                        />
                    </div>

                    {/* Stats Cards */}
                    <div className="grid grid-nogutter surface-section border-round-lg p-3 surface-border border-1">
                        <div className="col-12 flex align-items-center justify-content-between">
                            <div className="flex align-items-center gap-3">
                                <div className="flex align-items-center justify-content-center bg-blue-100 border-round-md icon-container">
                                    <i className="pi pi-bolt text-blue-600 text-xl"></i>
                                </div>
                                <div>
                                    <span className="block text-500 font-medium text-sm mb-1">Velocidad</span>
                                    <div className="text-900 font-medium text-lg">
                                        {itemsPerSecond.toFixed(1)} <span className="text-500 text-sm font-normal">items/seg</span>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="text-right">
                                <span className="block text-500 font-medium text-sm mb-1">Procesados</span>
                                <div className="text-900 font-medium text-lg">
                                    {processedItems} <span className="text-500 text-sm font-normal">/ {totalItems || '?'}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Terminal Output */}
                    <div className="flex flex-column gap-2">
                        <div className="flex align-items-center justify-content-between">
                            <label className="text-700 font-medium text-sm"><i className="pi pi-terminal mr-2"></i>Log del Sistema</label>
                        </div>
                        <div className="surface-900 p-3 border-round-lg shadow-1 overflow-hidden">
                            <div className="overflow-auto custom-scrollbar log-container" style={{ maxHeight: '320px' }}>
                                {logs.length === 0 ? (
                                    <div className="text-500 flex align-items-center justify-content-center h-full opacity-50">
                                        <span>Esperando inicio del proceso...</span>
                                    </div>
                                ) : (
                                    logs.map((log, index) => (
                                        <div key={index} className="flex gap-2 mb-1 hover:surface-800 p-1 border-round-xs transition-colors transition-duration-150">
                                            <span className="text-500 select-none flex-shrink-0 log-timestamp">{log.timestamp}</span>
                                            <span className={`flex-grow-1 ${log.type === 'error' ? 'text-pink-500' : log.type === 'success' ? 'text-green-400' : 'text-gray-300'} log-message`}>
                                                {log.type === 'error' && <i className="pi pi-times-circle mr-2"></i>}
                                                {log.type === 'success' && <i className="pi pi-check-circle mr-2"></i>}
                                                {log.message}
                                            </span>
                                        </div>
                                    ))
                                )}
                                <div ref={logsEndRef} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Sincronizacion;